import { useSnackbar } from 'contexts/snackbar';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import quizService from 'services/quiz';
import submissionService from 'services/submission';
import { selectSubmissionsForPushback } from 'utils/gradePushback';
import { createGradePushRequest, createTaskLimiter, GRADE_PUSH_CONCURRENCY } from 'utils/gradePushQueue';
import { getCanvasQuizGrade, getQuizGradeSnapshot } from 'utils/quizScoring';
import { v4 as uuid } from 'uuid';

// Quiz requests use saved grading and the combined score while sharing the assignment dialog's state and UI.
const useQuizGradePushback = ({
    quiz,
    submissions,
    pushbackType,
    onPushed,
    fetchAllLmsSubmissions,
    pushRecords,
    pushing,
    setLoading,
    setFilteredLoading,
    setFilteredSubmissions,
    setStep,
    setProgress,
    setIsSucceed,
}) => {
    const { t } = useTranslation('quiz');
    const { message } = useSnackbar();
    const [plan, setPlan] = useState(null);
    const mounted = useRef(true);
    const pushIds = useRef(new Map());
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const showError = (reason, fallback) => {
        const response = reason?.response ?? reason;
        message.error(response?.data?.message ?? reason?.message ?? fallback);
    };

    const review = async () => {
        if (!quiz || pushing.current) return;
        pushing.current = true;
        setFilteredLoading(true);
        try {
            const [savedQuiz, savedSubmissions, assignment] = await Promise.all([
                quizService.getQuiz(quiz.id),
                quizService.listQuizSubmissions(quiz.id),
                apiService.getAssignment({
                    course_id: quiz.course_id,
                    assignment_id: quiz.api_assignment_id,
                    accessToken: localStorage.getItem('access_token'),
                }),
            ]);
            if (!mounted.current) return;
            if (
                savedQuiz.id !== quiz.id ||
                savedQuiz.course_id !== quiz.course_id ||
                !Array.isArray(savedSubmissions)
            ) {
                throw new Error(
                    t('push.load-error', 'Quiz grades and Canvas status could not be loaded. Please try again.')
                );
            }
            const lmsSubmissions = await fetchAllLmsSubmissions(
                savedQuiz.course_id,
                savedQuiz.api_assignment_id,
                localStorage.getItem('access_token')
            );
            if (!mounted.current) return;
            const submissionIds = new Set(submissions.map(({ id }) => id));
            const eligible = savedSubmissions.filter(
                (submission) =>
                    submissionIds.has(submission.id) &&
                    getCanvasQuizGrade(getQuizGradeSnapshot(submission, savedQuiz), assignment.points_possible) != null
            );
            const selected = selectSubmissionsForPushback(eligible, lmsSubmissions, pushbackType, savedQuiz);
            const selectedIds = new Set(selected.map(({ id }) => id));
            eligible.forEach((submission) => {
                if (pushRecords.current.has(submission.id) && !selectedIds.has(submission.id))
                    selected.push(submission);
            });
            setPlan({ quiz: savedQuiz, submissions: selected, pointsPossible: assignment.points_possible });
            setFilteredSubmissions(selected);
            setProgress(0);
            setStep('review');
        } catch (reason) {
            if (mounted.current) showError(reason, t('common:message.something-went-wrong', 'Something went wrong.'));
        } finally {
            pushing.current = false;
            if (mounted.current) setFilteredLoading(false);
        }
    };

    const push = async () => {
        if (pushing.current || !plan?.submissions.length) return;
        pushing.current = true;
        setLoading(true);
        setProgress(0);
        setStep('pushing');
        const limit = createTaskLimiter(GRADE_PUSH_CONCURRENCY);
        const request = createGradePushRequest((body) => {
            if (!mounted.current) throw new Error('Quiz push dialog was closed.');
            return apiService.postGrade(body);
        });
        try {
            const outcomes = await Promise.allSettled(
                plan.submissions.map((source) =>
                    limit(async () => {
                        if (!mounted.current) return;
                        let result = pushRecords.current.get(source.id);
                        if (!result) {
                            if (!pushIds.current.has(source.id)) pushIds.current.set(source.id, uuid());
                            result = await request({
                                type: 'quiz',
                                quiz_id: plan.quiz.id,
                                submission_id: source.id,
                                push_id: pushIds.current.get(source.id),
                                pushback_type: pushbackType,
                                expected_modified_on: source.modified_on,
                                expected_quiz_modified_on: plan.quiz.modified_on,
                                expected_lms_points: plan.pointsPossible,
                            });
                            if (!result?.record)
                                throw new Error(
                                    t(
                                        'push.result-error',
                                        'Canvas did not confirm the push. Review its grade status before retrying.'
                                    )
                                );
                            pushRecords.current.set(source.id, result);
                        }
                        if (!result.history_saved) {
                            const saved = (await quizService.listQuizSubmissions(quiz.id)).find(
                                ({ id }) => id === source.id
                            );
                            if (!saved)
                                throw new Error(
                                    t(
                                        'push.missing-submission',
                                        'The quiz submission no longer exists. Reload this page.'
                                    )
                                );
                            const history = [...(saved.grade_pushback_history ?? [])];
                            if (!history.some(({ push_id }) => push_id === result.record.push_id))
                                history.push(result.record);
                            result.submission = await submissionService.updateSubmission(source.id, {
                                grade_pushback_history: history,
                            });
                            result.history_saved = true;
                        }
                        onPushed?.(source, result);
                        if (mounted.current) setProgress((current) => current + 1);
                    })
                )
            );
            if (!mounted.current) return;
            const failure = outcomes.find(({ status }) => status === 'rejected');
            if (failure) {
                showError(
                    failure.reason,
                    t('common:message.grades-push-fail', 'Failed to push some grades. Please try again later.')
                );
                setStep('review');
            } else {
                setIsSucceed(true);
                message.success(
                    t('common:message.grades-push-success', 'All grades have been successfully pushed to LMS.')
                );
            }
        } finally {
            pushing.current = false;
            if (mounted.current) setLoading(false);
        }
    };

    const reset = () => {
        pushIds.current.clear();
        setPlan(null);
    };
    return { review, push, reset };
};

export default useQuizGradePushback;
